import { storefrontOriginFor } from './storefront';

/**
 * Table QR helpers (pure, unit-testable).
 *
 * A QR code encodes the storefront URL for a location plus the table token:
 *   https://<slug>.adop.app/t/<qr_token>
 * The token is the only credential a guest has, so it never leaves the
 * owner/manager dashboard. These helpers only build URLs and sheets — the
 * actual QR image is rendered by the `qrcode` package in the page.
 */

/** Absolute URL a table QR code points at. Throws on a missing token. */
export function tableQrUrl(slug: string, qrToken: string, origin?: string): string {
  const token = qrToken.trim();
  if (token.length === 0) throw new Error('A table QR token is required.');
  const base = origin ?? storefrontOriginFor(slug);
  return `${base.replace(/\/+$/, '')}/t/${encodeURIComponent(token)}`;
}

/** URL-safe random token for a new table. 128 bits of entropy. */
export function newTableQrToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface TableLike {
  id: string;
  label: string;
  seats: number | null;
  qr_token: string;
  active: boolean;
}

export interface PrintableTableQr {
  tableId: string;
  label: string;
  seats: number | null;
  url: string;
}

export interface PrintableTableSheet {
  restaurantName: string;
  locationName: string;
  slug: string;
  tables: PrintableTableQr[];
}

/** Natural sort so "Table 2" comes before "Table 10". */
export function compareTableLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Active tables in label order, each with its absolute QR URL. Inactive tables
 * are omitted because printing a QR for a retired table would be misleading.
 */
export function buildTableQrSheet(
  restaurantName: string,
  location: { name: string; slug: string },
  tables: TableLike[],
  origin?: string,
): PrintableTableSheet {
  const resolvedOrigin = origin ?? storefrontOriginFor(location.slug);
  return {
    restaurantName,
    locationName: location.name,
    slug: location.slug,
    tables: tables
      .filter((table) => table.active)
      .slice()
      .sort((a, b) => compareTableLabels(a.label, b.label))
      .map((table) => ({
        tableId: table.id,
        label: table.label,
        seats: table.seats,
        url: tableQrUrl(location.slug, table.qr_token, resolvedOrigin),
      })),
  };
}

/** Filename-safe name for a downloaded QR image. */
export function tableQrFilename(label: string): string {
  const safe = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return safe.length > 0 ? `table-${safe}.png` : 'table-qr.png';
}
